import { FC } from 'react';
import { DesignerSetting } from '@app/DesignerSetting';
import { defaultServiceContext } from '@app/contexts';
import { useDesign } from '@app/hooks';
import { NavLayout } from '@app/page-components/shared';
import {
  Design,
  RouteConfig,
  UserFeatureFlag
} from '@app/types';
import { filterRouteConfigs } from '@app/utilities';
import { Utility } from '@app/utilities/Utility';
import { SettingsAboutPage } from './about/page';
import { SettingsBasicPage } from './basic/page';
import { SettingsCcxmlPage } from './ccxml/page';
import { SettingsFillerPromptPage } from './filler-prompts/page';
import { SettingsHauPage } from './hau/page';
import { SettingsLiveAgentPage } from './live-agent/page';
import { SettingsLocalePage } from './locale/page';
import { SettingsPropertiesPage } from './properties/page';
import { SettingsWizardConfigPage } from './wizard-config/page';

const { featureFlagService } = defaultServiceContext;

enum SettingsRoute {
  about = 'about',
  basic = 'basic',
  ccxml = 'ccxml',
  fillerPrompts = 'filler_prompts',
  hau = 'hau',
  liveAgent = 'liveAgent',
  locale = 'locale',
  properties = 'properties',
  wizardConfig = 'wizardConfig'
}

function getRoutes(serialized: Design): RouteConfig<SettingsRoute>[] {
  const labels = window.l.settings;
  const inventoryOn = featureFlagService.isFeatureOn(UserFeatureFlag.inventory);
  const isCommonModule = Utility.getIsCommonModule(serialized);
  const wizardConfigOn = featureFlagService.isFeatureOn(UserFeatureFlag.wizardConfig);
  const isPublic = DesignerSetting.s.is_public === 'true';

  return [
    {
      component: SettingsBasicPage,
      label: labels.application_basic,
      path: SettingsRoute.basic,
      tooltip: 'The Application Basic tab sets a variety of rules for the design.'
    },
    {
      component: SettingsPropertiesPage,
      condition: !isCommonModule,
      label: labels.properties,
      path: SettingsRoute.properties,
      tooltip: 'This tab allows you to specify properties for unit testing. Use the table in this tab to specify key-value-pairs that define properties for the application test instance.'
    },
    {
      component: SettingsLocalePage,
      label: labels.locale,
      path: SettingsRoute.locale,
      tooltip: 'The Locale tab allows you to translate prompts into other languages and select which languages are used in the design. You can also select a specific voice to articulate the prompts.'
    },
    {
      component: SettingsHauPage,
      condition: !isCommonModule,
      label: 'HAU Optional',
      path: SettingsRoute.hau,
      tooltip: 'Settings to use if HAU is disabled at any of the inputs in this application.'
    },
    {
      component: SettingsFillerPromptPage,
      condition: !isCommonModule,
      label: labels.filler_prompts.title,
      path: SettingsRoute.fillerPrompts,
      tooltip: 'Filler (such as "Please hold on while I find that information" or the sound of typing) can be played to the caller when the application has to pause speaking while it accomplishes a task. Use this tab to configure filler prompts and typing sounds.'
    },
    {
      component: SettingsLiveAgentPage,
      condition: !isCommonModule,
      label: labels.dtmf_live_agent,
      path: SettingsRoute.liveAgent,
      tooltip: 'Some applications may include a DTMF code that allows callers to bypass the Virtual Assistant and contact a live agent directly. (This code might only be known to a subset of callers with special privileges or roles within the client organization.) Type the code in this tab.'
    },
    {
      component: SettingsWizardConfigPage,
      condition: isCommonModule && wizardConfigOn,
      label: 'Import Config',
      path: SettingsRoute.wizardConfig
    },
    {
      component: SettingsCcxmlPage,
      condition: !isCommonModule && !isPublic,
      label: labels.ccxml_editor,
      path: SettingsRoute.ccxml,
      tooltip: 'This tab provides a coding environment for developers to edit the CCXML code used to communicate between the voice application and the voice browser. This tab is usually hidden from non-developers.'
    },
    {
      component: SettingsAboutPage,
      condition: !isCommonModule && inventoryOn,
      label: 'About',
      path: SettingsRoute.about
    }
  ];
}


/**
 * Route host for `/app/settings/<settings-route>`.
 */
export const SettingsLayout: FC = () => {
  const serialized = useDesign();
  const routes = filterRouteConfigs(getRoutes(serialized));

  return (
    <NavLayout
      defaultRoute={SettingsRoute.basic}
      routes={routes}
    />
  );
};
